import { Button, CheckCircleIcon, ConsentOrderIcon, FormProgress } from "@fla/components";
import classNames from "classnames";
import { useConsentOrders } from "hooks/ConsentOrders";
import { useState } from "react";
import { useSelector } from "react-redux";

import { selectConsentOrdersBase } from "../consentOrdersSelectors";
import { nextSteps, steps } from "../helper";

/*
on button click, we will save change user collection field consent_orders = true
*/
export default function ConsentOrders() {
  const consentOrders = useSelector(selectConsentOrdersBase);

  const {
    getTargetRoute,
    consentOrderLastStep,
    isConsentOrderSubmitted,
    isConsentOrderCompleted,
    isAssetsDebtsCompleted,
    totalProgressStep,
    currentProgressStep,
    getCurrentAssetsDebtsStep
  } = useConsentOrders();

  const [openAccordions, setOpenAccordions] = useState<{ [key: number]: boolean }>({});

  const toggleAccordion = (idx: number) => {
    setOpenAccordions(prevState => ({
      ...prevState,
      [idx]: !prevState[idx]
    }));
  };

  const progressActionUi = (idx: number) => {
    const passedSteps =
      consentOrders && consentOrders.initialDetails
        ? Array(
            consentOrders.initialDetails.isCompleted
              ? consentOrders.initialDetails.currentStep
              : consentOrders.initialDetails.currentStep - 1
          )
            .fill({})
            .map((_, i) => i + 1)
        : [];

    if (idx === 0) {
      if (!passedSteps.includes(1))
        return (
          <Button className="w-fit px-5 py-1.5 uppercase bg-primary" onClick={getTargetRoute}>
            Start
          </Button>
        );
      else if (passedSteps.length && !passedSteps.includes(consentOrderLastStep))
        return (
          <Button className="w-fit px-5 py-1.5 uppercase" onClick={getTargetRoute}>
            Continue
          </Button>
        );
      else if (passedSteps.includes(consentOrderLastStep))
        return (
          <div className="md:w-28 pl-10">
            <CheckCircleIcon
              strokeColor="text-[#2F9D5E]"
              classes="w-10 h-10 md:w-8 md:h-8 lg:w-14 lg:h-14 text-[#2F9D5E]"
            />
          </div>
        );
      else return null;
    } else if (idx === 1) {
      if (isConsentOrderCompleted && getCurrentAssetsDebtsStep() === 0)
        return (
          <Button
            className="w-fit px-5 py-1.5 uppercase"
            onClick={getTargetRoute}
            disabled={!passedSteps.includes(consentOrderLastStep)}
          >
            Start
          </Button>
        );
      else if (isConsentOrderCompleted && getCurrentAssetsDebtsStep() < 11)
        return (
          <Button className="w-fit px-5 py-1.5 uppercase" onClick={getTargetRoute}>
            Continue
          </Button>
        );
      else if (isAssetsDebtsCompleted())
        return (
          <div className="md:w-28 pl-10">
            <CheckCircleIcon
              strokeColor="text-[#2F9D5E]"
              classes="w-10 h-10 md:w-8 md:h-8 lg:w-14 lg:h-14 text-[#2F9D5E]"
            />
          </div>
        );
      else return null;
    } else if (idx === 2) {
      if (isAssetsDebtsCompleted() && !isConsentOrderSubmitted)
        return (
          <Button className="w-fit px-5 py-1.5 uppercase" onClick={getTargetRoute}>
            Continue
          </Button>
        );
      else if (isAssetsDebtsCompleted() && isConsentOrderSubmitted)
        return (
          <div className="md:w-28 pl-10">
            <CheckCircleIcon
              strokeColor="text-[#2F9D5E]"
              classes="w-10 h-10 md:w-8 md:h-8 lg:w-14 lg:h-14 text-[#2F9D5E]"
            />
          </div>
        );
      else return null;
    } else return null;
  };

  return (
    <div className="w-full h-auto flex flex-col space-y-10 pt-10 pb-20 px-5 md:px-0">
      <div className="max-w-3xl flex flex-col items-center space-y-5 mx-auto">
        <ConsentOrderIcon classes="w-20 h-20" />
        <h2>Consent Orders</h2>
      </div>
      <div className="w-full max-w-3xl space-y-5 mx-auto bg-gray-200 p-5 rounded-xl">
        <h4 className="text-center">Overall Progress</h4>

        <FormProgress
          fill={currentProgressStep()}
          total={totalProgressStep()}
          trackColor="white"
          progressColor="primary"
          classes="w-full"
        />
      </div>
      <div className="max-w-3xl space-y-5 mx-auto">
        <h4 className="text-center">Tasks</h4>
        {steps({
          consentOrders,
          openAccordions,
          toggleAccordion
        }).map((step, idx) => {
          const inactive = (idx === 1 && !isConsentOrderCompleted) || (idx === 2 && !isAssetsDebtsCompleted());
          return (
            <div
              key={+idx}
              className={classNames("flex flex-col bg-white shadow-md rounded-lg p-5", {
                "bg-opacity-50 cursor-not-allowed": inactive
              })}
            >
              <div
                className={classNames("flex", {
                  "bg-opacity-50": inactive
                })}
              >
                <div className="w-20 md:w-28 flex flex-col items-start md:pl-3 justify-center">
                  <step.icon
                    classes={classNames("w-12 h-12 md:w-16 md:h-16", {
                      "text-opacity-20": inactive
                    })}
                  />
                </div>
                <div className="w-[calc(100%_-_80px)] md:w-[calc(100%_-_112px)] flex flex-col md:flex-row space-y-5 md:space-y-0">
                  <div className="w-full md:w-[calc(100%_-_160px)]">
                    <p className="font-bold text-gray-500">{step.label}</p>
                    <h5
                      className={classNames({
                        "text-gray-300": inactive
                      })}
                    >
                      {step.title}
                    </h5>
                    <div>
                      <p
                        className={classNames("mt-1", {
                          "text-gray-400": inactive
                        })}
                      >
                        {step.description}
                      </p>
                    </div>
                  </div>

                  <div className="w-full md:w-40 flex flex-col justify-center items-end">{progressActionUi(idx)}</div>
                </div>
              </div>

              {step.showMore && !inactive ? <div className="space-y-5 md:mt-10">{step.showMoreContent()}</div> : null}
            </div>
          );
        })}
      </div>

      {/* this block will be visible, once user submit their information */}
      {isConsentOrderSubmitted ? (
        <div className="max-w-3xl space-y-5 mx-auto border-[3px] p-5 md:p-10 rounded-lg border-green-700">
          <h4 className="text-center text-green-700">Next Steps</h4>
          <p className="w-full max-w-lg mx-auto text-center">
            You have finished the online portion and can close your browser. These are the steps that will happen next.
          </p>
          {nextSteps.map((step, idx) => (
            <div key={+idx} className="flex flex-col bg-white shadow-md rounded-lg p-5">
              <div className="flex">
                <div className="w-32 flex flex-col items-start pl-3 justify-center">
                  <step.icon />
                </div>
                <div className="w-[calc(100%_-_32px)]">
                  <h5>{step.title}</h5>
                  <div className="flex justify-between items-start">
                    <p className="mt-1">{step.description}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
